/* eslint-disable camelcase */
import { Grid, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DeleteRounded, SaveRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
import columns from './table_config';
import { useDispatch } from 'react-redux';
import styles from '../styles';
import { CONTROL_TABLE_DROPDOWN_VALUES, CONTROL_TABLE_PARAMETER_MAP, CONTROL_TABLE_STEP_SIZE_DIVISION_COUNT } from '../../../../../utils/constants';
import { generateRandom } from '../../../../../utils/randomNumberGenerator';
import { enqueueSnackbar } from '../../../../../store/actions/snackbar';
import MobileSelectEditInputTextField from '../../SelectEditInputTextField';
import MobileSelectEditValueTypes from '../../SelectEditValueTypes';
import DeleteConfirmationModal from '../../../../deleteConfirmationModal';
import MobileTableInputs from '../tableInputs';

function MobileControlsTable({ units, currentUnit, loading, targetData, setTargetData, setParameterDirty, setTargetSaveEnable, setControlsTableDirty, disableTable }) {
  const classes = styles();
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const [gridColumns, setGridColumns] = useState([]);
  const [scrollToNew, setScrollToNew] = useState(false);

  useEffect(() => {
    setGridColumns(columns(renderNameCell, renderValueCell, renderActionCell, units, currentUnit));
  }, []);

  useEffect(() => {
    const capitalize = (str) => {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };
    const getKeyByValue = (obj, value) => {
      return Object.keys(obj).find(key => obj[key] === value) || value;
    };
    if (properties.length === 0 && targetData?.controls_table) {
      const updatedData = targetData?.controls_table?.map((item, index) => ({
        ...item,
        priority: index === 0 ? 'First' : 'Second',
        maintain: item?.parameter === 'sbp' ? '-' : capitalize(item.maintain),
        parameter: getKeyByValue(CONTROL_TABLE_PARAMETER_MAP, item?.parameter),
        step_size: item?.parameter === 'sbp' ? '-' : item?.step_size,
        _id: generateRandom()
      }));
      setProperties(updatedData);
    }
  }, []);

  const handleAddControls = () => {
    if (properties[0]?.parameter === 'SBP') {
      dispatch(
        enqueueSnackbar(
          'Please select Rate as first priority to add a new row',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return;
    }
    const nextId = properties.length;
    const newRow = {
      row: properties.length + 1,
      priority: properties.length === 0 ? 'First' : 'Second',
      maintain: '',
      parameter: '',
      from: '',
      to: '',
      step_size: '',
      grid_id: nextId + 1,
      _id: generateRandom(),
      isNewRow: true,
      isDirty: true,
    };
    setProperties((prev) => [...prev, newRow]);
    setControlsTableDirty(true);
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      return prevProperties.map(item => {
        if (item._id !== id) return item;

        const updatedItem = { ...item };
        let dirty = false;

        const normalizedValue =
        typeof value === 'string' ? value : Number(value);

        if (normalizedValue !== item[field]) {
          updatedItem[field] = normalizedValue;
          dirty = true;
        }

        if (field === 'parameter' && normalizedValue === 'SBP') {
          if (item.maintain !== '-') {
            updatedItem.maintain = '-';
            dirty = true;
          }
          if (item.step_size !== '-') {
            updatedItem.step_size = '-';
            dirty = true;
          }
        } else if (field === 'parameter' && normalizedValue === 'Rate') {
          if (item.maintain === '-') {
            updatedItem.maintain = '';
            dirty = true;
          }
          if (item.step_size === '-') {
            updatedItem.step_size = '';
            dirty = true;
          }
        }

        return {
          ...updatedItem,
          isDirty: dirty || item.isDirty
        };
      });
    });

    setControlsTableDirty(true);
  };

  const checkRowValues = (value) => {
    const row = value;
    const invalidFields = [];
    if (row?.parameter !== 'SBP' && (row?.maintain === undefined || row?.maintain === null || row?.maintain === '')) invalidFields.push('Maintain');
    if (row?.parameter === undefined || row?.parameter === null || row?.parameter === '') invalidFields.push('Parameter');
    if (row?.from === undefined || row?.from === null || row?.from === '') invalidFields.push('From');
    if (row?.to === undefined || row?.to === null || row?.to === '') invalidFields.push('To');
    if (row?.parameter !== 'SBP' && (row?.step_size === undefined || row?.step_size === null || row?.step_size === '')) invalidFields.push('Step Size');
    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
            `Please enter the following details: ${invalidFields.join(', ')}`,
            'error',
            new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const validateProperties = (row) => {
    const { from, to, step_size, parameter, _id } = row;
    if (Number(from) < units[CONTROL_TABLE_PARAMETER_MAP[parameter]]?.min || Number(from) > units[CONTROL_TABLE_PARAMETER_MAP[parameter]]?.max) {
      setInvalidRows(prev => ({ ...prev, [_id]: true }));
      dispatch(
        enqueueSnackbar(
          `From should be between ${units[CONTROL_TABLE_PARAMETER_MAP[parameter]]?.min} and ${units[CONTROL_TABLE_PARAMETER_MAP[parameter]]?.max}.`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    if (Number(to) < units[CONTROL_TABLE_PARAMETER_MAP[parameter]]?.min || Number(to) > units[CONTROL_TABLE_PARAMETER_MAP[parameter]]?.max) {
      setInvalidRows(prev => ({ ...prev, [_id]: true }));
      dispatch(
        enqueueSnackbar(
          `To should be between ${units[CONTROL_TABLE_PARAMETER_MAP[parameter]]?.min} and ${units[CONTROL_TABLE_PARAMETER_MAP[parameter]]?.max}.`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    const range = to - from;
    const stepSize = Number(step_size);

    const minStepSize = range / CONTROL_TABLE_STEP_SIZE_DIVISION_COUNT.min;
    const maxStepSize = range / CONTROL_TABLE_STEP_SIZE_DIVISION_COUNT.max;

    const isStepSizeInvalid = parameter !== 'SBP' && (stepSize < minStepSize || stepSize > maxStepSize);

    if (isStepSizeInvalid) {
      setInvalidRows(prev => ({ ...prev, [_id]: true }));
      dispatch(
        enqueueSnackbar(
              `Step size should be between ${minStepSize.toFixed(1)} and ${maxStepSize.toFixed(1)}.`,
              'error',
              new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    if (Number(to) <= Number(from)) {
      setInvalidRows(prev => ({ ...prev, [_id]: true }));
      dispatch(
        enqueueSnackbar(
          'To should be greater than From value.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
    return true;
  };

  function checkForDuplicates(properties) {
    const seen = new Set();

    for (let i = 0; i < properties.length; i++) {
      const { parameter } = properties[i];
      const key = parameter;

      if (seen.has(key)) {
        setInvalidRows(prev => ({ ...prev, [properties[i]?._id]: true }));
        dispatch(
          enqueueSnackbar(
            'Row with parameter already exists',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        return false;
      }
      seen.add(key);
    }
    return true;
  }

  const handleSave = (row) => {
    if (checkRowValues(row)) {
      if (checkForDuplicates(properties) && validateProperties(row)) {
        setInvalidRows(prev => ({ ...prev, [row._id]: false }));
        return true;
      }
    }
  };

  const markRowAsSaved = (id) => {
    let updated = [];
    setProperties((prevProperties) => {
      updated = prevProperties.map((item) => {
        if (item._id === id) {
          // eslint-disable-next-line no-unused-vars
          const { isDirty, isNewRow, ...rest } = item;
          return { ...rest };
        }
        return item;
      });
      return updated;
    });
    setTimeout(() => {
      setTargetData(prev => ({
        ...prev,
        controls_table: updated
      }));
      setControlsTableDirty(false);
      setTargetSaveEnable(true);
    }, 0);

    setParameterDirty(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updatedDirty = [...prev];
      updatedDirty[0] = { ...updatedDirty[0], isDirty: true };
      return updatedDirty;
    });
  };

  const removeRunsECDSpan = (row) => {
    const updated = properties?.filter(item => item._id !== row?._id)?.map(item => ({ ...item, priority: 'First' })) || [];
    setProperties(updated);
    const anyDirty = updated.some(item => item?.isDirty);
    if (!anyDirty) {
      setControlsTableDirty(false);
    }
    setParameterDirty(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updatedDirty = [...prev];
      updatedDirty[0] = { ...updatedDirty[0], isDirty: true };
      return updatedDirty;
    });
    setTargetData(prev => ({
      ...prev,
      controls_table: updated
    }));
    setTargetSaveEnable(true);
    setDeleteAction(false);
    setDeleteRow({});
  };

  const renderValueCell = (params) => {
    return <MobileSelectEditInputTextField {...params} updateData={updateData} loading={params?.row?.parameter === 'SBP' && (params?.field === 'maintain' || params?.field === 'step_size')} />;
  };

  const renderNameCell = (params) => {
    const { field, row } = params;

    let dropdownData = CONTROL_TABLE_DROPDOWN_VALUES[field] || [];

    if (field === 'parameter' && row?.priority === 'Second') {
      dropdownData = dropdownData.filter(item => item === 'SBP');
    }

    return <MobileSelectEditValueTypes {...params} closeCellAfterSelect={true} updateData={updateData} data={dropdownData} loading={params?.row?.parameter === 'SBP' && (params?.field === 'maintain' || params?.field === 'step_size')} />;
  };

  const renderActionCell = ({ row }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || loading}
          onClick={() => {
            if (handleSave(row)) {
              setTimeout(() => {
                markRowAsSaved(row._id);
              }, 100);
            }
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={loading}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  return (
    <Grid container>
        <Grid container alignItems='center' item marginLeft='12px' marginTop='20px'>
          <Grid>
            <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>Run&apos;s  Controls Table</Typography>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item marginLeft='12px' className={classes.customTool}>
            <Typography variant='button' fontWeight='700' onClick={handleAddControls} className={loading || properties.length === 2 || properties?.some(item => item.isDirty === true) || disableTable ? classes.disableIcon : ''}>+ Add</Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <MobileTableInputs
              data={properties}
              columns={gridColumns}
              updateData={updateData}
              loading={loading}
              scrollToNew={scrollToNew}
              onScrolled={() => setScrollToNew(false)}
              invalidRows={invalidRows}
              disableTable={disableTable}
            />
        </Grid>
          {
              deleteAction && <DeleteConfirmationModal
                  open={deleteAction}
                  handleDelete={removeRunsECDSpan}
                  handleClose={setDeleteAction}
                  deleteRow={deleteRow}
              />
          }
      </Grid>
  );
}

MobileControlsTable.propTypes = {
  units: PropTypes.object,
  currentUnit: PropTypes.string,
  runsECDSpanNames: PropTypes.array,
  loading: PropTypes.bool,
  targetData: PropTypes.object,
  setTargetData: PropTypes.func,
  setParameterDirty: PropTypes.func,
  setTargetSaveEnable: PropTypes.func,
  setControlsTableDirty: PropTypes.func,
  disableTable: PropTypes.bool
};

export default MobileControlsTable;
